import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import {
  Building,
  Home,
  TrendingUp,
  TrendingDown,
  Wrench,
  FileText,
  DollarSign,
  Users,
  AlertCircle,
  CheckCircle2,
  Clock
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell
} from 'recharts';

// Import role-specific dashboards
import ResidentDashboard from './ResidentDashboard';
import EmployeeDashboard from './EmployeeDashboard';
import BoardDashboard from './BoardDashboard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const COLORS = ['#4f46e5', '#7c3aed', '#06b6d4', '#10b981', '#f59e0b'];

/*
 * Altam™ Operating Services
 * Main Dashboard - Routes to Role-Specific Dashboard
 * Copyright (c) 2026 Pecaro Consulting Inc. All rights reserved.
 */

export default function Dashboard() {
  const { user } = useAuth();

  // Route to role-specific dashboard
  if (user?.role === 'resident') {
    return <ResidentDashboard />;
  }

  if (user?.role === 'employee') {
    return <EmployeeDashboard />;
  }

  if (user?.role === 'board') {
    return <BoardDashboard />;
  }

  // Admin and Manager see the full dashboard
  return <AdminDashboard />;
}

// Admin/Manager/Board Dashboard Component
function AdminDashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [recentTransactions, setRecentTransactions] = useState([]);
  const [maintenanceRequests, setMaintenanceRequests] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [statsRes, maintenanceRes] = await Promise.all([
        axios.get(`${API}/dashboard/stats`),
        axios.get(`${API}/maintenance?status=open`)
      ]);

      setStats(statsRes.data);
      setRecentTransactions(statsRes.data.recent_transactions || []);
      setMaintenanceRequests(maintenanceRes.data.slice(0, 5));
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: 'CAD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  // AR/AP data for chart
  const arApData = [
    { name: 'Current', ar: stats?.ar_aging?.current || 0, ap: stats?.ap_aging?.current || 0 },
    { name: '1-30 Days', ar: stats?.ar_aging?.days_30 || 0, ap: stats?.ap_aging?.days_30 || 0 },
    { name: '31-60 Days', ar: stats?.ar_aging?.days_60 || 0, ap: stats?.ap_aging?.days_60 || 0 },
    { name: '61-90 Days', ar: stats?.ar_aging?.days_90 || 0, ap: stats?.ap_aging?.days_90 || 0 },
    { name: '90+ Days', ar: stats?.ar_aging?.over_90 || 0, ap: stats?.ap_aging?.over_90 || 0 }
  ];

  return (
    <div className="space-y-6" data-testid="dashboard">
      {/* Welcome Header */}
      <div className="bg-gradient-to-r from-indigo-600 to-violet-600 rounded-xl p-6 text-white">
        <h1 className="text-2xl font-bold">Welcome back, {user?.first_name || 'User'}!</h1>
        <p className="text-indigo-100 mt-1">
          Here's your financial overview and property management summary.
        </p>
      </div>

      {/* Primary Financial Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border border-slate-200 p-5 hover:shadow-md transition-shadow">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm font-medium text-slate-500">Total Revenue</p>
              <p className="text-2xl font-bold text-emerald-600 mt-1">{formatCurrency(stats?.total_revenue)}</p>
              <p className="text-xs text-slate-400 mt-1">Income across all properties</p>
            </div>
            <div className="bg-emerald-500 p-3 rounded-xl">
              <TrendingUp className="w-6 h-6 text-white" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 p-5 hover:shadow-md transition-shadow">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm font-medium text-slate-500">Total Expenses</p>
              <p className="text-2xl font-bold text-rose-600 mt-1">{formatCurrency(stats?.total_expenses)}</p>
              <p className="text-xs text-slate-400 mt-1">Outgoing payments</p>
            </div>
            <div className="bg-rose-500 p-3 rounded-xl">
              <TrendingDown className="w-6 h-6 text-white" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 p-5 hover:shadow-md transition-shadow">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm font-medium text-slate-500">Net Income</p>
              <p className={`text-2xl font-bold mt-1 ${(stats?.net_income || 0) >= 0 ? 'text-indigo-600' : 'text-rose-600'}`}>
                {formatCurrency(stats?.net_income)}
              </p>
              <p className="text-xs text-slate-400 mt-1">Revenue minus expenses</p>
            </div>
            <div className="bg-indigo-500 p-3 rounded-xl">
              <DollarSign className="w-6 h-6 text-white" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 p-5 hover:shadow-md transition-shadow">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm font-medium text-slate-500">Bank Balance</p>
              <p className="text-2xl font-bold text-violet-600 mt-1">{formatCurrency(stats?.bank_balance)}</p>
              <p className="text-xs text-slate-400 mt-1">Combined account balances</p>
            </div>
            <div className="bg-violet-500 p-3 rounded-xl">
              <Building className="w-6 h-6 text-white" />
            </div>
          </div>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center gap-3">
            <div className="bg-emerald-100 p-2 rounded-lg">
              <CheckCircle2 className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <p className="text-xl font-bold text-slate-900">{stats?.collection_rate || 0}%</p>
              <p className="text-xs text-slate-500">Collection Rate</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center gap-3">
            <div className="bg-amber-100 p-2 rounded-lg">
              <Wrench className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="text-xl font-bold text-slate-900">{stats?.open_work_orders || 0}</p>
              <p className="text-xs text-slate-500">Open Work Orders</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center gap-3">
            <div className="bg-cyan-100 p-2 rounded-lg">
              <FileText className="w-5 h-5 text-cyan-600" />
            </div>
            <div>
              <p className="text-xl font-bold text-slate-900">{stats?.pending_invoices || 0}</p>
              <p className="text-xs text-slate-500">Pending Invoices</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center gap-3">
            <div className="bg-rose-100 p-2 rounded-lg">
              <AlertCircle className="w-5 h-5 text-rose-600" />
            </div>
            <div>
              <p className="text-xl font-bold text-slate-900">{stats?.overdue_invoices || 0}</p>
              <p className="text-xs text-slate-500">Overdue Invoices</p>
            </div>
          </div>
        </div>
      </div>

      {/* Property Stats Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-slate-50 rounded-xl p-4">
          <div className="flex items-center gap-2">
            <Building className="w-4 h-4 text-slate-500" />
            <span className="text-sm text-slate-500">Properties</span>
          </div>
          <p className="text-2xl font-bold text-slate-900 mt-1">{stats?.total_properties || 0}</p>
        </div>
        <div className="bg-slate-50 rounded-xl p-4">
          <div className="flex items-center gap-2">
            <Home className="w-4 h-4 text-slate-500" />
            <span className="text-sm text-slate-500">Units</span>
          </div>
          <p className="text-2xl font-bold text-slate-900 mt-1">{stats?.total_units || 0}</p>
          <p className="text-xs text-slate-400">{stats?.occupied_units || 0} occupied</p>
        </div>
        <div className="bg-slate-50 rounded-xl p-4">
          <div className="flex items-center gap-2">
            <Users className="w-4 h-4 text-slate-500" />
            <span className="text-sm text-slate-500">Leases</span>
          </div>
          <p className="text-2xl font-bold text-slate-900 mt-1">{stats?.active_leases || 0}</p>
          <p className="text-xs text-slate-400">Active</p>
        </div>
        <div className="bg-slate-50 rounded-xl p-4">
          <div className="flex items-center gap-2">
            <Users className="w-4 h-4 text-slate-500" />
            <span className="text-sm text-slate-500">Employees</span>
          </div>
          <p className="text-2xl font-bold text-slate-900 mt-1">{stats?.employees || 0}</p>
          <p className="text-xs text-slate-400">Active</p>
        </div>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Revenue Trend */}
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <h3 className="text-lg font-semibold text-slate-900 mb-4">Revenue Trend</h3>
          <ResponsiveContainer width="100%" height={250}>
            <LineChart data={stats?.revenue_trend || []}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="month" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} />
              <Tooltip formatter={(value) => formatCurrency(value)} />
              <Line type="monotone" dataKey="amount" stroke="#10b981" strokeWidth={3} dot={{ fill: '#10b981' }} name="Revenue" />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Expense Trend */}
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <h3 className="text-lg font-semibold text-slate-900 mb-4">Expense Trend</h3>
          <ResponsiveContainer width="100%" height={250}>
            <LineChart data={stats?.expense_trend || []}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="month" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} />
              <Tooltip formatter={(value) => formatCurrency(value)} />
              <Line type="monotone" dataKey="amount" stroke="#ef4444" strokeWidth={3} dot={{ fill: '#ef4444' }} name="Expenses" />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* AR/AP Aging Chart */}
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <h3 className="text-lg font-semibold text-slate-900 mb-4">Accounts Receivable / Payable Aging</h3>
        <ResponsiveContainer width="100%" height={280}>
          <BarChart data={arApData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis dataKey="name" tick={{ fontSize: 12 }} />
            <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} />
            <Tooltip formatter={(value) => formatCurrency(value)} />
            <Bar dataKey="ar" fill="#4f46e5" name="Receivable (AR)" radius={[4, 4, 0, 0]} />
            <Bar dataKey="ap" fill="#f59e0b" name="Payable (AP)" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
        <div className="flex justify-center gap-6 mt-4">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-indigo-600 rounded"></div>
            <span className="text-sm text-slate-600">Accounts Receivable</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-amber-500 rounded"></div>
            <span className="text-sm text-slate-600">Accounts Payable</span>
          </div>
        </div>
      </div>

      {/* Bottom Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Transactions */}
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <h3 className="text-lg font-semibold text-slate-900 mb-4">Recent Transactions</h3>
          <div className="space-y-3">
            {recentTransactions.length === 0 ? (
              <p className="text-slate-500 text-sm">No recent transactions</p>
            ) : (
              recentTransactions.map((tx) => (
                <div key={tx.id} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${tx.type === 'income' ? 'bg-emerald-100' : 'bg-rose-100'}`}>
                      <DollarSign className={`w-4 h-4 ${tx.type === 'income' ? 'text-emerald-600' : 'text-rose-600'}`} />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-slate-900">{tx.description}</p>
                      <p className="text-xs text-slate-500">{tx.date}</p>
                    </div>
                  </div>
                  <span className={`font-semibold ${tx.type === 'income' ? 'text-emerald-600' : 'text-rose-600'}`}>
                    {tx.type === 'income' ? '+' : '-'}{formatCurrency(Math.abs(tx.amount))}
                  </span>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Open Maintenance Requests */}
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <h3 className="text-lg font-semibold text-slate-900 mb-4">Open Maintenance Requests</h3>
          <div className="space-y-3">
            {maintenanceRequests.length === 0 ? (
              <div className="flex items-center justify-center py-8 text-slate-500">
                <CheckCircle2 className="w-5 h-5 mr-2 text-emerald-500" />
                All caught up! No open requests.
              </div>
            ) : (
              maintenanceRequests.map((req) => (
                <div key={req.id} className="flex items-start justify-between py-2 border-b border-slate-100 last:border-0">
                  <div className="flex items-start gap-3">
                    <div className={`p-2 rounded-lg ${
                      req.priority === 'high' ? 'bg-rose-100' : 
                      req.priority === 'medium' ? 'bg-amber-100' : 'bg-slate-100'
                    }`}>
                      <Wrench className={`w-4 h-4 ${
                        req.priority === 'high' ? 'text-rose-600' : 
                        req.priority === 'medium' ? 'text-amber-600' : 'text-slate-600'
                      }`} />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-slate-900">{req.title}</p>
                      <p className="text-xs text-slate-500">{req.unit_number} • {req.entity_name}</p>
                    </div>
                  </div>
                  <span className={`text-xs px-2 py-1 rounded-full ${
                    req.status === 'open' ? 'bg-rose-100 text-rose-700' : 'bg-amber-100 text-amber-700'
                  }`}>
                    {req.status}
                  </span>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
